<template>
    <b-container>
        <b-row>
            <b-col>
                <b-table
                    :fields="table.fields"
                    :items="table.items"
                    class="mb-3 mt-3"
                    striped
                    show-empty
                    responsive
                    empty-text="No Contacts to show"
                    outlined
                    :tbody-tr-class="rowClass">
                    <template #table-busy>
                      <div class="text-center text-danger my-2">
                        <b-spinner class="align-middle"></b-spinner>
                        <strong>Loading...</strong>
                      </div>
                    </template>
                    <template #cell(note_text)="row">
                        <pre>
                            {{row.item.note_text}}
                        </pre>
                    </template>
                    <template #cell(message_status)="row">
                        <b-badge :variant="getStatusVariant(row.item.message_status)" class="status-badge">
                            {{formatStatus(row.item.message_status)}}
                        </b-badge>
                        <b-button
                            v-if="hasError(row.item)"
                            size="sm"
                            variant="link"
                            class="p-0 ml-2"
                            @click="row.toggleDetails"
                            :aria-label="row.detailsShowing ? 'Hide error details' : 'Show error details'">
                            <b-icon :icon="row.detailsShowing ? 'chevron-up' : 'chevron-down'"></b-icon>
                        </b-button>
                    </template>
                    <template #cell(created_on)="row">
                        {{$moment.utc(row.item.created_on).local().format('lll')}}
                    </template>
                    <template #cell(contact_name)="row">
                        <router-link :to="`/ward/${wardId}/contact/${row.item.contact.contact_id}/notes`">
                            {{row.item.contact.first_name}} {{row.item.contact.last_name}}
                        </router-link>
                    </template>
                    <template #cell(organization)="row">
                        {{getOrganizationInfo(row.item.contact.primary_organization).text}}
                    </template>
                    <template #cell(aux_organization)="row">
                        {{flattenAuxOrganizations(row.item.contact.aux_organization)}}
                    </template>
                    
                    <!-- Row details for error information -->
                    <template #row-details="row">
                        <b-card bg-variant="danger" text-variant="white" class="error-details-card">
                            <b-card-text>
                                <strong>❌ Error Details</strong>
                                <div class="mt-2">
                                    <div><strong>Error Code:</strong> {{row.item.message_error || 'Unknown'}}</div>
                                    <div class="mt-2"><strong>Description:</strong> {{getErrorDescription(row.item.message_error)}}</div>
                                    <div class="mt-2">
                                        <a :href="getErrorDocsUrl(row.item.message_error)" 
                                           target="_blank" 
                                           rel="noopener"
                                           class="text-white">
                                            <u>View Twilio Documentation →</u>
                                        </a>
                                    </div>
                                    <div v-if="row.item.event_type" class="mt-2">
                                        <small><strong>Event Type:</strong> {{row.item.event_type}}</small>
                                    </div>
                                </div>
                            </b-card-text>
                        </b-card>
                    </template>
                </b-table>
            </b-col>
        </b-row>
        <b-row>
            <b-col align="center">
                <b-button-group v-if="pages.length>1">
                    <b-button @click="searchContacts(currentPage-1)">Prev</b-button>
                    <b-button @click="searchContacts(currentPage+1)">Next</b-button>
                </b-button-group>
            </b-col>
        </b-row>
    </b-container>
</template>
<script>
import { MessageApi } from "@/app/messages/messageApi";
import handleError from "@/errorHandler";

// Common SMS Error Codes Mapping (same as Statistics component)
const COMMON_SMS_ERROR_CODES = {
  '21211': {
    title: "Invalid 'To' Phone Number",
    description: "The phone number is not in a valid format.",
    docs_url: "https://www.twilio.com/docs/api/errors/21211"
  },
  '21614': {
    title: "Not a Mobile Number",
    description: "The number is a landline or cannot receive SMS.",
    docs_url: "https://www.twilio.com/docs/api/errors/21614"
  },
  '21610': {
    title: "Unsubscribed Recipient",
    description: "The recipient previously replied STOP to unsubscribe.",
    docs_url: "https://www.twilio.com/docs/api/errors/21610"
  },
  '30007': {
    title: "Message Filtered",
    description: "The carrier rejected this message.",
    docs_url: "https://www.twilio.com/docs/api/errors/30007"
  },
  '30008': {
    title: "Unknown Destination",
    description: "Cannot route to this phone number.",
    docs_url: "https://www.twilio.com/docs/api/errors/30008"
  },
  '30006': {
    title: "Landline or Unreachable",
    description: "The number is unreachable or a landline.",
    docs_url: "https://www.twilio.com/docs/api/errors/30006"
  },
  '30005': {
    title: "Unknown Destination Handset",
    description: "Unable to deliver to this handset.",
    docs_url: "https://www.twilio.com/docs/api/errors/30005"
  },
  '30004': {
    title: "Message Blocked",
    description: "Message delivery is blocked by carrier.",
    docs_url: "https://www.twilio.com/docs/api/errors/30004"
  },
  '21408': {
    title: "Permission to Send SMS Denied",
    description: "Your account doesn't have permission to send to this number.",
    docs_url: "https://www.twilio.com/docs/api/errors/21408"
  },
  '21612': {
    title: "'From' Number Not Supported",
    description: "The From number is not SMS-capable.",
    docs_url: "https://www.twilio.com/docs/api/errors/21612"
  }
};

export default {
    name: "MessageContactList",
    props: {
        wardId: {
            type: String,
            required: false,
            default: null
        },
        messageId: {
            type: String,
            required: false,
            default: null
        },
        organization: {
            type: String,
            required: false,
            default: null
        },
        messageState: {
            type: String,
            required: false,
            default: null
        },
        organizations: {
            type: Array,
            required: false,
            default: []
        }
    },
    data () {
        return {
            searchTerm: null,
            nextPage: {
                aux_id: null,
                pk_id: null,
                gsi_pk: null,
                gsi_sk: null
            },
            prevPage: {
                aux_id: null,
                pk_id: null,
                gsi_pk: null,
                gsi_sk: null
            },
            pages: [],
            currentPage: 0,
            lastPage: 0,
            table: {
                id: "message-contact_list",
                fields: [
                    {
                      key: "message_status",
                      label: "Message Status"
                    },
                    {
                        key: "contact_name",
                        label: "Contact Name"
                    },
                    {
                        key: "contact.phone_number",
                        label: "Phone Number"
                    },
                    {
                        key: "organization",
                        label: "Organization"
                    },
                    {
                        key: "aux_organization",
                        label: "Auxilary Organizations"
                    },
                    {
                        key: "created_on",
                        label: "Sent On"
                    },
                    {
                        key: "added_by.username",
                        label: "Sent By"
                    }
                ],
                items: [],
                isBusy: false,
                totalRows: 0,
                page: {
                    index: 1,
                    size: 5
                }

            }

        }
    },
    async mounted() {
        this.pages.push(this.nextPage);
        await this.searchContacts(0);
    },
    methods: {
        async searchContacts(page) {
            if(page < 0) {
                return;
            }
            if(page > this.pages.length - 1) {
                return;
            }
            const payload =  this.pages[page];
            this.table.isBusy = true;
            payload.search_term = this.searchTerm;
            payload.message_state = 'PENDING';
            try {
                const { data } = await MessageApi.getMessageContacts(this.wardId, this.messageId, payload);
                if (data.last_evaluated_key !== null) {
                    if(!this.existInPageList(data.last_evaluated_key, this.pages))
                        this.pages.push(data.last_evaluated_key);
                }
                this.table.items = data.message_contact_list;
                this.table.isBusy = false;
                this.currentPage = page;
            } catch (e) {
                handleError(e, "Failed to fetch message notes");
            }
        },
        existInPageList(obj, list) {
            if(list.length==0) return false;
            var i;
            for (i = 0; i < list.length; i++) {
                if(list[i].pk_id === obj.pk_id && list[i].aux_id === obj.aux_id) {
                    return true;
                }
            }
            return false;
        },
        async refreshContactList() {
            this.pages = [];
            this.currentPage = 0;
            this.lastPage = 0;
            this.pages.push(this.nextPage);
            await this.searchContacts(0);
        },
        getOrganizationInfo(value) {
            // console.log(`Value: ${value}`)
            if (value===null || value==="None") return {"text":"Guest-Not Available"};
            var i;
            for (i=0; i < this.organizations.length; i++) {
                if(this.organizations[i].value==value)
                    return this.organizations[i];
            }
        },

        flattenAuxOrganizations(list_of_orgs) {
            if(list_of_orgs === undefined) return 'None';
            if(list_of_orgs.length==0) return 'None';
            return list_of_orgs.map(x=>
            {
                let orgInfo = this.getOrganizationInfo(x);
                if (orgInfo === undefined)
                    return x;
                if (orgInfo.disabled)
                    return `${orgInfo.text} [Disabled]`;
                return orgInfo.text;
            }).join(', ');

        },
        async filterContacts() {
            this.pages = [];
            this.currentPage = 0;
            this.lastPage = 0;
            this.pages.push(this.nextPage);
            await this.searchContacts(0);
        },
        hasError(item) {
            return item.message_status === 'failed' || item.message_status === 'undelivered';
        },
        getStatusVariant(status) {
            const variants = {
                'delivered': 'success',
                'failed': 'danger',
                'undelivered': 'warning',
                'read': 'info',
                'sent': 'secondary',
                'queued': 'light',
                'sending': 'primary'
            };
            return variants[status] || 'secondary';
        },
        formatStatus(status) {
            if (!status) return 'Pending';
            return status.charAt(0).toUpperCase() + status.slice(1);
        },
        rowClass(item, type) {
            if (!item || type !== 'row') return '';
            if (item.message_status === 'failed' || item.message_status === 'undelivered') {
                return 'table-danger-subtle';
            }
            return '';
        },
        getErrorDescription(errorCode) {
            if (!errorCode) return "An error occurred during delivery.";
            const errorInfo = COMMON_SMS_ERROR_CODES[errorCode];
            if (errorInfo) {
                return `${errorInfo.title}: ${errorInfo.description}`;
            }
            return `Error ${errorCode}: An error occurred during delivery. Click the link for more details.`;
        },
        getErrorDocsUrl(errorCode) {
            if (!errorCode) {
                return "https://www.twilio.com/docs/api/errors";
            }
            const errorInfo = COMMON_SMS_ERROR_CODES[errorCode];
            if (errorInfo) {
                return errorInfo.docs_url;
            }
            return `https://www.twilio.com/docs/api/errors/${errorCode}`;
        }
    }


}
</script>
<style lang="scss" scoped>
         pre {
            overflow-x: auto;
            white-space: pre-wrap;
            white-space: -moz-pre-wrap;
            white-space: -pre-wrap;
            white-space: -o-pre-wrap;
            word-wrap: break-word;
         }
         
         .status-badge {
            font-size: 0.9rem;
            padding: 0.4em 0.8em;
         }
         
         .error-details-card {
            margin: 0.5rem 0;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
         }
         
         ::v-deep .table-danger-subtle {
            background-color: #f8d7da !important;
         }
</style>
